"use client";

import { Routes } from "@/api";
import { ChatRoomTokenQuery } from "@/hooks/queries/chat/room/use-chat-room-token-query";
import { useToggle } from "@/hooks/use-toggle";
import { getServerUrl } from "@/utils/chat/get-livekit-url";
import { LiveKitRoom, RoomAudioRenderer } from "@livekit/components-react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { PropsWithChildren } from "react";

type CallContextType = {
  token: string | undefined;
  toggle: ReturnType<typeof useToggle>;
  channelId: {
    value: string | undefined;
    update: React.Dispatch<React.SetStateAction<string | undefined>>;
  };
  activeSpeakerVisible: boolean;
};

const CallContext = React.createContext<CallContextType | undefined>(undefined);

export function CallProvider({ children }: PropsWithChildren) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isVideoRoom = searchParams?.get("type") === "video";
  const roomToken = searchParams?.get("rt");
  const [channelId, setChannelId] = React.useState<string | undefined>(
    undefined,
  );
  const roomTokenToggle = useToggle(isVideoRoom);
  const chatRoomTokenQuery = ChatRoomTokenQuery.useQuery(channelId ?? "", {
    enabled: Boolean(channelId && roomTokenToggle.value),
  });

  const activeSpeakerVisible =
    !pathname?.includes(Routes.INBOX_CHAT(channelId ?? "-")) || !isVideoRoom;

  const leaveRoom = React.useCallback(() => {
    roomTokenToggle.setOff();
    if (!isVideoRoom) {
      return;
    }
    // Only go back if the user is in a video room
    React.startTransition(() => {
      router.back();
    });
  }, [isVideoRoom, roomTokenToggle, router]);

  React.useEffect(
    function initQueryFromParams() {
      if (!roomToken || !channelId) {
        return;
      }

      ChatRoomTokenQuery.setData(channelId, roomToken);
      roomTokenToggle.setOn();
    },
    [channelId, roomToken, roomTokenToggle],
  );

  const token = roomToken || chatRoomTokenQuery.data;
  const serverUrl = getServerUrl(roomToken);

  return (
    <CallContext.Provider
      value={{
        token,
        toggle: roomTokenToggle,
        channelId: {
          value: channelId,
          update: setChannelId,
        },
        activeSpeakerVisible,
      }}
    >
      <LiveKitRoom
        token={
          roomTokenToggle.value || isVideoRoom || roomToken
            ? (token ?? "")
            : undefined
        }
        serverUrl={serverUrl}
        onDisconnected={leaveRoom}
        className="!h-auto"
      >
        {children}

        <RoomAudioRenderer />
      </LiveKitRoom>
    </CallContext.Provider>
  );
}

export const useCallContext = () => {
  const context = React.useContext(CallContext);
  if (context === undefined) {
    throw new Error(`CallContext must be used within a RoomProvider`);
  }
  return context;
};
